// cart-upsells -- fills the cart's "You might also like" grid.
//
// The section (sections/cart-native-upsells.liquid) renders any pinned
// products server-side plus skeleton placeholders. This script asks a
// recommendations feed for product ids seeded from what's in the cart, fetches
// each product's card as Liquid (/products/<handle>?view=upsell-card, which is
// snippets/cart-upsell-card.liquid) and swaps the skeletons for real cards.
//
// Feed, in order:
//   Rebuy   GET https://rebuyengine.com/api/v1/products/recommended?key=...
//           or  https://rebuyengine.com/api/v1/custom/id/<ruleset>?key=...
//           when data-rebuy-key is set on the grid (Theme settings -> Cart
//           recommendations). Same endpoints the Feetures theme uses.
//   Shopify GET /recommendations/products.json?product_id=<id>&intent=related
//           otherwise -- one call per seed product, merged in order.
//
// Card interactions (variant picker, quantity tiers, add to cart) are
// delegated on document so they survive the cart re-rendering itself, which
// cart.js does on every quantity change by replacing [data-cart-content]. That
// re-render also brings back fresh skeletons, so we refill on cart:updated.

(function () {
  'use strict';

  if (window.__cartUpsellsLoaded) return;
  window.__cartUpsellsLoaded = true;

  var REBUY_RECOMMENDED_URL = 'https://rebuyengine.com/api/v1/products/recommended';
  var REBUY_CUSTOM_URL = 'https://rebuyengine.com/api/v1/custom/id/';
  // Product types that never belong in "you might also like": add-ons sell
  // from the line item's own accordion, samples are a PDP-only path.
  var EXCLUDED_TYPES = ['add-on', 'sample'];

  // Card HTML per handle, kept across refills so a quantity change on the cart
  // doesn't refetch every card.
  var cardCache = new Map();
  var inflight = null;

  function parseIds(value) {
    return String(value || '')
      .split(',')
      .map(function (s) { return s.trim(); })
      .filter(Boolean);
  }

  async function fetchRebuy(grid, seedIds, limit) {
    var key = grid.dataset.rebuyKey;
    var ruleset = grid.dataset.rebuyRuleset;
    var url = new URL(ruleset ? REBUY_CUSTOM_URL + encodeURIComponent(ruleset) : REBUY_RECOMMENDED_URL);
    url.searchParams.set('key', key);
    if (seedIds.length) url.searchParams.set('shopify_product_ids', seedIds.join(','));
    url.searchParams.set('limit', String(limit));
    url.searchParams.set('filter_oos', 'yes');
    var r = await fetch(url.toString(), { credentials: 'omit' });
    if (!r.ok) throw new Error('Rebuy ' + r.status);
    var data = await r.json();
    var products = data.data || data.products || (Array.isArray(data) ? data : []);
    return products.map(function (p) {
      return { id: String(p.id), handle: p.handle, type: p.product_type || p.type || '' };
    });
  }

  async function fetchShopify(seedIds, limit) {
    // One request per seed, merged round-robin so a two-product cart gets
    // recommendations for both rather than just the first.
    var lists = await Promise.all(
      seedIds.map(function (id) {
        var url = '/recommendations/products.json?product_id=' + encodeURIComponent(id) + '&limit=' + Math.min(limit, 10) + '&intent=related';
        return fetch(url)
          .then(function (r) { return r.ok ? r.json() : { products: [] }; })
          .then(function (d) { return d.products || []; })
          .catch(function () { return []; });
      })
    );
    var merged = [];
    var max = Math.max.apply(null, lists.map(function (l) { return l.length; }).concat(0));
    for (var i = 0; i < max; i++) {
      lists.forEach(function (l) { if (l[i]) merged.push(l[i]); });
    }
    return merged.map(function (p) {
      return { id: String(p.id), handle: p.handle, type: p.type || p.product_type || '' };
    });
  }

  async function fetchCard(handle) {
    if (cardCache.has(handle)) return cardCache.get(handle);
    var html = await fetch('/products/' + encodeURIComponent(handle) + '?view=upsell-card')
      .then(function (r) { return r.ok ? r.text() : ''; })
      .catch(function () { return ''; });
    html = html.trim();
    cardCache.set(handle, html);
    return html;
  }

  async function fill(grid) {
    var limit = parseInt(grid.dataset.limit, 10) || 3;
    var seedIds = parseIds(grid.dataset.seedIds);
    var exclude = new Set(parseIds(grid.dataset.excludeIds));
    var skeletons = grid.querySelectorAll('[data-upsell-skeleton]');
    var slots = skeletons.length;
    if (!slots) return;

    var candidates = [];
    try {
      candidates = grid.dataset.rebuyKey
        ? await fetchRebuy(grid, seedIds, limit * 3)
        : await fetchShopify(seedIds, limit * 2);
    } catch (e) {
      console.warn('[cart-upsells]', e);
      if (grid.dataset.rebuyKey) {
        // Rebuy down or misconfigured: Shopify's engine is always there.
        candidates = await fetchShopify(seedIds, limit * 2).catch(function () { return []; });
      }
    }

    var seen = new Set();
    var picked = candidates.filter(function (p) {
      if (!p.handle || seen.has(p.id) || exclude.has(p.id)) return false;
      if (EXCLUDED_TYPES.indexOf(String(p.type).toLowerCase()) !== -1) return false;
      seen.add(p.id);
      return true;
    });

    // Fetch cards for more than we need: a product can render empty (no
    // purchasable variant) and we'd rather not show a hole.
    var htmls = await Promise.all(picked.slice(0, slots + 2).map(function (p) { return fetchCard(p.handle); }));
    var cards = htmls.filter(Boolean).slice(0, slots);

    // The grid may have been replaced while we were fetching.
    if (!document.contains(grid)) return;
    skeletons = grid.querySelectorAll('[data-upsell-skeleton]');
    cards.forEach(function (html, i) {
      if (!skeletons[i]) return;
      var tpl = document.createElement('template');
      tpl.innerHTML = html;
      skeletons[i].replaceWith(tpl.content.firstElementChild);
    });
    // Whatever skeletons are left had nothing to fill them.
    grid.querySelectorAll('[data-upsell-skeleton]').forEach(function (el) { el.remove(); });
    if (!grid.querySelector('[data-upsell-item]')) {
      var section = grid.closest('[data-cart-upsells]');
      if (section) section.hidden = true;
    }
  }

  var pending = false;
  function refill() {
    var grid = document.querySelector('[data-cart-upsells-grid]');
    if (!grid) return;
    // A cart re-render mid-fill leaves fresh skeletons behind; run again once
    // the current fill settles rather than racing two fills.
    if (inflight) { pending = true; return; }
    inflight = fill(grid)
      .catch(function (e) { console.warn('[cart-upsells]', e); })
      .then(function () {
        inflight = null;
        if (pending) { pending = false; refill(); }
      });
  }

  // ---- card interactions, delegated ------------------------------------

  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function updatePricing(item, quantitySelect) {
    var opt = quantitySelect.options[quantitySelect.selectedIndex];
    if (!opt) return;
    var quantity = parseInt(opt.value, 10);
    var unitPrice = parseInt(opt.dataset.unitPrice, 10);
    var total = unitPrice * quantity;
    var button = item.querySelector('[data-upsell-add]');
    item.querySelector('[data-unit-price-display]').textContent = money(unitPrice);
    item.querySelector('[data-quantity-display]').textContent = quantity.toLocaleString();
    button.querySelector('[data-button-text]').textContent = 'Add ' + quantity.toLocaleString() + ' \u00b7 ' + money(total);
    button.dataset.quantity = quantity;
    button.dataset.unitPrice = unitPrice;
    button.dataset.totalPrice = total;
  }

  function onVariantChange(item, variantSelect) {
    var opt = variantSelect.options[variantSelect.selectedIndex];
    var sku = opt.dataset.sku;
    var pricing = {};
    try { pricing = JSON.parse(item.dataset.allPricing || '{}'); } catch (e) {}
    var tiers = pricing[sku] || [];
    var quantitySelect = item.querySelector('[data-quantity-select]');
    var wanted = parseInt(item.dataset.quantity, 10);
    quantitySelect.innerHTML = '';
    tiers.forEach(function (tier) {
      var o = document.createElement('option');
      o.value = String(tier.min_qty);
      o.dataset.unitPrice = Math.round(parseFloat(tier.unit_price) * 100);
      o.textContent = tier.min_qty + ' \u00b7 ' + money(o.dataset.unitPrice) + ' ea';
      if (parseInt(tier.min_qty, 10) === wanted) o.selected = true;
      quantitySelect.appendChild(o);
    });
    if (opt.dataset.image) {
      var img = item.querySelector('.upsell-image img');
      if (img) img.src = opt.dataset.image;
    }
    item.querySelector('[data-upsell-add]').dataset.variantId = opt.value;
    if (quantitySelect.options.length) updatePricing(item, quantitySelect);
  }

  async function onAdd(button) {
    var variantId = button.dataset.variantId;
    var quantity = parseInt(button.dataset.quantity, 10);
    if (!variantId || !quantity) return;
    var text = button.querySelector('[data-button-text]');
    var original = text.textContent;
    button.disabled = true;
    button.classList.add('loading');
    text.textContent = 'Adding…';
    try {
      // Send the properties cart.js would otherwise stamp on afterwards
      // (stampNewItems), so the add is one render instead of two.
      var delivery = document.querySelector('[data-form-delivery-timeframe]');
      var item = button.closest('[data-upsell-item]');
      var r = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity,
          properties: {
            _customProductId: (item && item.dataset.productHandle ? item.dataset.productHandle : 'item') + '_' + Date.now(),
            _deliveryOption: delivery ? delivery.value : '',
          },
        }),
      });
      if (!r.ok) throw new Error('add ' + r.status);
      // cart.js re-renders the cart, which brings the new line in with its
      // own tier dropdown and gives the item a _customProductId.
      document.dispatchEvent(new CustomEvent('cart:reload'));
    } catch (e) {
      console.error('[cart-upsells]', e);
      button.disabled = false;
      button.classList.remove('loading');
      text.textContent = original;
      alert('Could not add ' + (button.dataset.productTitle || 'that item') + ' to your cart. Please try again.');
    }
  }

  document.addEventListener('change', function (e) {
    var item = e.target.closest && e.target.closest('[data-upsell-item]');
    if (!item) return;
    if (e.target.matches('[data-variant-select]')) onVariantChange(item, e.target);
    else if (e.target.matches('[data-quantity-select]')) updatePricing(item, e.target);
  });

  document.addEventListener('click', function (e) {
    var button = e.target.closest && e.target.closest('[data-upsell-add]');
    if (button) return onAdd(button);
    var toggle = e.target.closest && e.target.closest('[data-upsell-options-toggle]');
    if (toggle) {
      var options = toggle.closest('[data-upsell-item]').querySelector('[data-upsell-options]');
      var open = options.hidden;
      options.hidden = !open;
      toggle.setAttribute('aria-expanded', String(open));
      toggle.classList.toggle('is-open', open);
    }
  });

  document.addEventListener('cart:updated', refill);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refill);
  } else {
    refill();
  }
})();
